<template>
  <v-app :dark="darkTheme">
    <!-- Only show Toolbar and navigation drawer when not on auth-related routes -->
    <template v-if="!isAuthRelatedRoute">
      <Toolbar
        :plan="plan"
        :credits="credits"
        @toggle-drawer="drawer = !drawer"
      />
      <!-- Per-app toolbar extension point (e.g. a secondary action bar). -->
      <slot name="toolbar-extra"></slot>
      <v-navigation-drawer
        app
        clipped
        v-model="drawer"
        :permanent="$vuetify.breakpoint.mdAndUp"
        :temporary="!$vuetify.breakpoint.mdAndUp"
        :mini-variant="sideMenuMini && $vuetify.breakpoint.mdAndUp"
        :mini-variant-width="64"
        :width="sideMenuWidth"
        :dark="darkTheme"
        class="side-menu"
      >
        <!-- Resize handle (expanded desktop mode only) -->
        <div
          v-if="!sideMenuMini && $vuetify.breakpoint.mdAndUp"
          class="side-menu-resize-handle"
          title="Drag to resize (double-click to reset)"
          @mousedown="onSideMenuResizeStart"
          @dblclick="onSideMenuResizeReset"
        ></div>
        <!-- Collapse toggle (desktop): shrink the nav to an icon rail for
             more workspace room. Persisted per user. -->
        <div v-if="$vuetify.breakpoint.mdAndUp" class="side-menu-collapse-row">
          <v-btn icon x-small @click="toggleSideMenuMini">
            <v-icon small>
              {{ sideMenuMini ? "mdi-chevron-right" : "mdi-chevron-left" }}
            </v-icon>
          </v-btn>
        </div>
        <div class="side-menu-content">
          <!-- Primary navigation -->
          <v-list dense nav class="side-menu-nav">
            <v-list-item
              v-for="item in navItems"
              :key="item.to"
              :to="item.to"
              router
              exact
            >
              <v-list-item-icon>
                <v-icon>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ item.title }}</v-list-item-title>
            </v-list-item>
          </v-list>

          <!-- Account navigation: generic pages every app ships with.
               Override via the accountNavItems prop; [] hides the section. -->
          <template v-if="visibleAccountNavItems.length">
            <v-divider></v-divider>
            <v-list dense nav class="side-menu-nav">
              <v-subheader
                v-if="!(sideMenuMini && $vuetify.breakpoint.mdAndUp)"
              >
                Account
              </v-subheader>
              <v-list-item
                v-for="item in visibleAccountNavItems"
                :key="item.to"
                :to="item.to"
                router
                exact
              >
                <v-list-item-icon>
                  <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </template>

          <!-- Admin-only navigation -->
          <template v-if="isAdmin">
            <v-divider></v-divider>
            <v-list dense nav class="side-menu-nav">
              <v-subheader
                v-if="!(sideMenuMini && $vuetify.breakpoint.mdAndUp)"
              >
                Admin
              </v-subheader>
              <v-list-item
                v-for="item in visibleAdminNavItems"
                :key="item.to"
                :to="item.to"
                router
                exact
              >
                <v-list-item-icon>
                  <v-icon>{{ item.icon }}</v-icon>
                </v-list-item-icon>
                <v-list-item-title>{{ item.title }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </template>

          <!-- Per-app drawer extension point (e.g. extra nav sections). -->
          <slot name="drawer-append"></slot>

          <v-spacer class="side-menu-spacer"></v-spacer>

          <!-- Anonymous trial guests: a sign-up CTA instead of the profile
               menu (a guest has no email, profile, or billing to manage). -->
          <div class="side-menu-profile" v-if="isAnonymousUser">
            <v-btn
              color="primary"
              block
              depressed
              to="/register"
              class="anon-signup-btn"
            >
              <v-icon left small>mdi-account-plus</v-icon>
              Sign up free
            </v-btn>
          </div>

          <!-- Profile section at bottom -->
          <div class="side-menu-profile" v-else-if="currentUser">
            <v-menu offset-y top min-width="180">
              <template v-slot:activator="{ on, attrs }">
                <div class="profile-button" v-bind="attrs" v-on="on">
                  <v-avatar size="32" class="profile-avatar">
                    <span class="profile-initials">{{ userInitials }}</span>
                  </v-avatar>
                  <div class="profile-info">
                    <div class="profile-email">{{ currentUser.email }}</div>
                    <div class="profile-plan" v-if="!requiresPaidUpgrade && plan">
                      {{ plan }}
                    </div>
                  </div>
                </div>
              </template>
              <v-list>
                <v-list-item to="/profile" router>
                  <v-list-item-icon>
                    <v-icon>mdi-account-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Profile</v-list-item-title>
                </v-list-item>
                <v-list-item v-if="apiKeysEnabled" to="/api-keys" router>
                  <v-list-item-icon>
                    <v-icon>mdi-key</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>API Keys</v-list-item-title>
                </v-list-item>
                <v-list-item
                  to="/subscription"
                  router
                  :disabled="requiresPaidUpgrade"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-card-account-details</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>My Subscription</v-list-item-title>
                </v-list-item>
                <v-list-item
                  to="/billing"
                  router
                  :disabled="requiresPaidUpgrade"
                >
                  <v-list-item-icon>
                    <v-icon>mdi-credit-card-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Billing</v-list-item-title>
                </v-list-item>
                <v-divider></v-divider>
                <v-list-item to="/logout" router>
                  <v-list-item-icon>
                    <v-icon>mdi-logout</v-icon>
                  </v-list-item-icon>
                  <v-list-item-title>Logout</v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>
          </div>
        </div>
      </v-navigation-drawer>
    </template>
    <v-main :class="{ 'standalone-main': isAuthRelatedRoute }">
      <!-- MAINTENANCE MODE banner: unmissable on every page while engaged. -->
      <div
        v-if="!isAuthRelatedRoute && $store.state.maintenanceMode"
        class="maintenance-banner"
      >
        <v-icon left color="white">mdi-wrench-outline</v-icon>
        MAINTENANCE MODE - some features are temporarily unavailable. Thanks for
        your patience.
      </div>
      <!-- Global Alert for subscription and credit notifications -->
      <GlobalAlert
        v-if="!isAuthRelatedRoute && !requiresPaidUpgrade"
        :subscription="$store.getters.currentSubscription"
        :creditBalance="$store.getters.creditBalance"
      />
      <div class="router-view-wrapper">
        <slot name="before-page"></slot>
        <router-view />
      </div>

      <!-- Credit Warning Dialog -->
      <CreditWarningDialog />

      <!-- App-wide finished-work notice (store.activeWork) -->
      <v-snackbar
        v-model="workToast.show"
        :color="workToast.color"
        :timeout="15000"
        bottom
        right
      >
        {{ workToast.text }}
        <template #action="{ attrs }">
          <v-btn v-if="workToast.route" text v-bind="attrs" @click="openWorkToast">
            View
          </v-btn>
          <v-btn icon v-bind="attrs" @click="workToast.show = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </template>
      </v-snackbar>
    </v-main>
  </v-app>
</template>

<script>
import Toolbar from "@shared/components/Toolbar.vue";
import { apiClient } from "@shared/services/api";
import GlobalAlert from "@shared/components/GlobalAlert.vue";
import CreditWarningDialog from "@shared/components/CreditWarningDialog.vue";
import userActivityService from "@shared/services/userActivityService";
import { formatPlanName } from "@shared/utils/planLabels";
import { safeInternalPath } from "@shared/utils/safePath";
import { resetWidth, startColumnDrag } from "@shared/utils/columnResize";

export default {
  name: "AppShell",
  components: {
    Toolbar,
    GlobalAlert,
    CreditWarningDialog
  },
  props: {
    // Primary drawer navigation: array of { icon, title, to }.
    navItems: {
      type: Array,
      default: () => []
    },
    // Account drawer navigation: the generic pages every app has. Apps
    // rarely need to touch this - pass [] to hide it or a custom array to
    // reorder/extend. Same shape as navItems.
    accountNavItems: {
      type: Array,
      default: () => [
        { icon: "mdi-card-account-details-outline", title: "Subscription", to: "/subscription" },
        { icon: "mdi-credit-card-outline", title: "Billing", to: "/billing" },
        { icon: "mdi-gift-outline", title: "Invite Friends", to: "/invite-friends" }
      ]
    },
    // Admin-only drawer navigation (shown when store.getters.isAdmin),
    // same shape as navItems.
    adminNavItems: {
      type: Array,
      default: () => []
    }
  },
  data() {
    var sideMenuMini = false;
    try {
      sideMenuMini = localStorage.getItem("sideMenuMini") === "1";
    } catch (e) {
      /* private mode */
    }
    var sideMenuWidth = 200;
    try {
      var storedMenuWidth = parseInt(
        localStorage.getItem("colWidth:sideMenu") || "",
        10
      );
      if (!isNaN(storedMenuWidth)) sideMenuWidth = storedMenuWidth;
    } catch (e) {
      /* private mode */
    }
    return {
      drawer: true,
      sideMenuMini: sideMenuMini,
      sideMenuWidth: sideMenuWidth,
      // App-wide "your work is ready" notice (store.activeWork watcher).
      workTimer: null,
      workErrors: 0,
      workToast: { show: false, text: "", color: "success", route: null }
    };
  },
  computed: {
    visibleAdminNavItems() {
      return this.adminNavItems.filter(item => {
        const route = this.$router.resolve(item.to).route;
        return this.$store.getters.isPlatformAdmin || !route.matched.some(record => record.meta.requiresPlatformAdmin);
      });
    },
    darkTheme: {
      get() {
        return this.$store.getters.darkTheme;
      },
      set(value) {
        this.$store.commit("SET_DARK_THEME", value);
      }
    },
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    // An app without API keys hides the menu entry (API_KEYS_ENABLED: false).
    apiKeysEnabled() {
      return !(this.$config && this.$config.API_KEYS_ENABLED === false);
    },
    plan() {
      const subscription = this.$store.getters.currentSubscription;
      // Empty until the account data has loaded: the toolbar and the profile
      // menu show no plan rather than flashing "Loading...".
      if (
        (this.$store.getters.isAuthenticated ||
          this.$store.getters.currentUser) &&
        !this.$store.getters.accountDataLoaded
      ) {
        return "";
      }
      return formatPlanName(subscription && subscription.subscription_plan);
    },
    credits() {
      const creditBalance = this.$store.getters.creditBalance;
      if (
        (this.$store.getters.isAuthenticated ||
          this.$store.getters.currentUser) &&
        !this.$store.getters.accountDataLoaded
      ) {
        return "...";
      }
      const balance =
        creditBalance && creditBalance.balance ? creditBalance.balance : 0;
      // Format to max 2 decimal places, removing trailing zeros
      return parseFloat(balance.toFixed(2));
    },
    isAuthRelatedRoute() {
      // Auth screens and explicitly standalone public routes do not use the
      // signed-in application shell.
      const authRoutes = [
        "/login",
        "/register",
        "/forgot-password",
        "/reset-password",
        "/verify-email"
      ];

      const isAuthPath = authRoutes.some(
        route =>
          this.$route.path === route || this.$route.path.startsWith(route + "/")
      );
      const usesStandaloneLayout = this.$route.matched.some(
        record => record.meta && record.meta.hideAppChrome
      );
      return isAuthPath || usesStandaloneLayout;
    },
    currentUser() {
      return this.$store.getters.currentUser;
    },
    isAnonymousUser() {
      return !!(this.currentUser && this.currentUser.is_anonymous);
    },
    // Guests keep Subscription/Billing (both render fine and feed the
    // funnel) but referral pages make no sense without a real account -
    // and apps with no referral programme at all
    // (config.REFERRALS_ENABLED === false) never show them.
    visibleAccountNavItems() {
      var referralsEnabled = !(
        this.$config && this.$config.REFERRALS_ENABLED === false
      );
      if (referralsEnabled && !this.isAnonymousUser) {
        return this.accountNavItems;
      }
      return this.accountNavItems.filter(function (item) {
        return item.to !== "/invite-friends";
      });
    },
    requiresPaidUpgrade() {
      return this.$store.getters.requiresPaidUpgrade;
    },
    userInitials() {
      if (!this.currentUser || !this.currentUser.name) return "";

      const nameParts = this.currentUser.name.split(" ");
      if (nameParts.length === 1) {
        const name = nameParts[0];
        return name.length >= 2
          ? name.charAt(0).toUpperCase() + name.charAt(1).toUpperCase()
          : name.charAt(0).toUpperCase();
      } else {
        return (
          nameParts[0].charAt(0).toUpperCase() +
          nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        );
      }
    }
  },
  created() {
    // Load public feature flags (force-paid, maintenance mode, ...) as early
    // as possible. The paid-gate watcher reacts once this and account data
    // have both loaded.
    this.$store.dispatch("fetchPublicConfig");

    // Hide drawer by default on mobile, show on desktop
    this.drawer = this.$vuetify.breakpoint.mdAndUp;

    // Store router instance for DialogService
    window.router = this.$router;

    // Public identity proofs must remain usable with an expired stored session.
    if (this.isAuthRelatedRoute) return;

    const isGuestRoute = this.$route.matched.some(
      record => record.meta && record.meta.guest
    );
    const hasToken = this.$store.getters.isAuthenticated;
    const hasStoredUser = !!this.$store.getters.currentUser;

    // Guest routes must remain accessible even if localStorage contains stale
    // user data without a valid token.
    if (isGuestRoute && hasStoredUser && !hasToken) {
      this.$store.commit("LOGOUT");
      return;
    }

    if (hasToken) {
      this.$store.dispatch("initializeAccountState").catch(error => {
        if (error.response && error.response.status === 401) {
          this.$store.dispatch("logout");
        } else {
          console.error("Unable to load account data");
        }
      });
    }

  },

  beforeDestroy() {
    document.removeEventListener("visibilitychange", this.handleReturnFocus);
    window.removeEventListener("focus", this.handleReturnFocus);
    this.stopWorkWatch();
    userActivityService.destroy();
  },
  watch: {
    // Keep watching one in-flight unit of work wherever the user navigates,
    // and announce the outcome if they are no longer on its page.
    "$store.state.activeWork": {
      immediate: true,
      handler(descriptor) {
        this.stopWorkWatch();
        if (descriptor && descriptor.statusUrl) {
          this.workErrors = 0;
          this.workTimer = setInterval(this.pollActiveWork, 5000);
        }
      }
    },
    "$vuetify.breakpoint.mdAndUp"(val) {
      // When breakpoint changes, update drawer state
      this.drawer = val;
    },
    // Force-paid gate backstop: covers the hard-refresh case where a gated user
    // loads a protected route before account data / public config have arrived.
    // Navigation-time enforcement lives in the router guard (router.js).
    requiresPaidUpgrade(val) {
      if (val) this.enforcePaidGate();
    }
  },

  mounted() {
    // Refresh account state when the user returns to this tab, so purchases
    // completed on Stripe (credit packs, subscriptions - possibly in another
    // tab) unlock the app without a manual reload: the paywall card, toolbar
    // credits and maintenance banner are all reactive on this data.
    document.addEventListener("visibilitychange", this.handleReturnFocus);
    window.addEventListener("focus", this.handleReturnFocus);
  },

  methods: {
    stopWorkWatch() {
      if (this.workTimer) {
        clearInterval(this.workTimer);
        this.workTimer = null;
      }
    },
    async pollActiveWork() {
      const descriptor = this.$store.state.activeWork;
      if (!descriptor) {
        this.stopWorkWatch();
        return;
      }
      // The page watching this work polls it itself (and clears the
      // registration on completion) - no need for a parallel request.
      if (descriptor.route && this.$route.path === descriptor.route) return;
      let status;
      try {
        const response = await apiClient.get(descriptor.statusUrl);
        status = response.data && response.data[descriptor.statusField || "status"];
        this.workErrors = 0;
      } catch (e) {
        // A few transient errors are fine; a dead endpoint (deleted image,
        // expired session) should not poll forever.
        this.workErrors += 1;
        if (this.workErrors >= 5) {
          this.$store.commit("CLEAR_ACTIVE_WORK", descriptor.key);
        }
        return;
      }
      const ready = (descriptor.readyStatuses || []).indexOf(status) !== -1;
      const failed = (descriptor.failStatuses || []).indexOf(status) !== -1;
      if (!ready && !failed) return;
      this.$store.commit("CLEAR_ACTIVE_WORK", descriptor.key);
      // The page watching this work shows the outcome itself.
      if (descriptor.route && this.$route.path === descriptor.route) return;
      this.workToast = {
        show: true,
        color: ready ? "success" : "warning",
        text: ready
          ? descriptor.readyText || "Your work is ready."
          : descriptor.failText || "Your work needs attention.",
        route: descriptor.route || null
      };
    },
    openWorkToast() {
      const route = this.workToast.route;
      this.workToast.show = false;
      if (route) this.$router.push(route).catch(function () {});
    },
    onSideMenuResizeStart(event) {
      var self = this;
      startColumnDrag(event, {
        key: "sideMenu",
        min: 160,
        max: 320,
        startWidth: this.sideMenuWidth,
        onWidth: function (px) {
          self.sideMenuWidth = px;
        }
      });
    },

    onSideMenuResizeReset() {
      var self = this;
      resetWidth("sideMenu", 200, function (px) {
        self.sideMenuWidth = px;
      });
    },

    toggleSideMenuMini() {
      this.sideMenuMini = !this.sideMenuMini;
      try {
        localStorage.setItem("sideMenuMini", this.sideMenuMini ? "1" : "0");
      } catch (e) {
        /* private mode */
      }
    },

    // Re-sync user / credits / subscription when the tab regains focus or
    // visibility, throttled so rapid alt-tabbing doesn't hammer the API.
    // Catch-all for payments completed outside this tab (Stripe checkout).
    handleReturnFocus() {
      if (document.visibilityState === "hidden") return;
      if (!this.$store.getters.isAuthenticated) return;
      const now = Date.now();
      if (this._lastReturnRefresh && now - this._lastReturnRefresh < 20000) {
        return;
      }
      this._lastReturnRefresh = now;
      this.$store.dispatch("fetchCurrentUser").catch(() => {});
      this.$store.dispatch("fetchCreditBalance").catch(() => {});
      this.$store.dispatch("fetchSubscription").catch(() => {});
      this.$store.dispatch("fetchPublicConfig").catch(() => {});
    },

    // -------------------------------------------------
    //  Force-paid gate
    // -------------------------------------------------
    /**
     * Redirect a gated free-tier user to the subscribe page. The router guard
     * (router.js) handles navigation attempts; this backstop handles the case
     * where account data arrives after the user is already on a protected page.
     * Pages a gated user is allowed to stay on (so we don't loop or trap them):
     * the subscribe page itself, subscription/billing management, and any
     * route the app flags allowWhenUnpaid (as the router guard does).
     */
    enforcePaidGate() {
      var allowed = ["Subscribe", "Subscription", "Billing"];
      if (allowed.indexOf(this.$route.name) !== -1) return;
      if (this.$route.path === "/subscribe") return;
      if (this.$route.matched.some(function (record) {
        return record.meta && record.meta.allowWhenUnpaid;
      })) return;
      var next = safeInternalPath(this.$route.fullPath);
      this.$router.push(next ? { path: "/subscribe", query: { next: next } } : "/subscribe").catch(function () {});
    }
  }
};
</script>

<style scoped>
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
  font-family: var(--font-body);
  background-color: var(--bg-primary);
}
.v-main {
  padding-top: 48px !important;
  /* Don't override left/right padding - Vuetify sets these based on navigation drawer */
}

.v-main.standalone-main {
  padding-top: 0 !important;
}

/* Router view wrapper - simple styling */
.router-view-wrapper {
  min-width: 0;
}

/* The toolbar is 48px tall on phones too (44px tap targets), so the top
   padding above stays the same at every width. */
.side-menu {
  background: var(--sidebar-bg) !important;
  border-right: 1.5px solid var(--sidebar-border) !important;
}

/* On mobile, sidebar needs higher z-index to appear above toolbar and overlays when opened */
@media (max-width: 960px) {
  .side-menu {
    z-index: 1001 !important;
  }
}
.side-menu .v-list {
  background: var(--sidebar-bg) !important;
}
.side-menu .v-list-item {
  border-radius: 8px;
  margin: 4px 8px;
  color: var(--text-primary) !important;
}
.side-menu .v-list-item.side-menu-active,
.side-menu .v-list-item--active {
  background-color: var(--overlay-light) !important;
  color: var(--text-heading) !important;
}
.side-menu .v-list-item-title {
  font-weight: 600;
  font-size: var(--font-size-sm);
  color: inherit !important;
}
.side-menu .v-list-item-icon {
  color: var(--text-heading) !important;
}

/* Mini (collapsed) sidebar: icon-only rail - the New task button shrinks to
   a centered icon (template swap) and the project list hides. */
.side-menu.v-navigation-drawer--mini-variant .side-menu-new-task {
  justify-content: center;
}

/* Side menu content layout - flex column to push profile to bottom */
.side-menu-collapse-row {
  display: flex;
  justify-content: flex-end;
  padding: 4px 6px 0;
}

.side-menu-resize-handle {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 10;
}

.side-menu-resize-handle:hover {
  background: rgba(25, 118, 210, 0.15);
}

/* The content takes the height left under the collapse row (the drawer's content
   element is a flex column, global style below): 100% pushed the profile and the
   guest sign-up button below the fold on a 900 px screen. */
.side-menu-content {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
}

.side-menu-nav {
  padding-top: 8px;
}

.side-menu-spacer {
  flex: 1 1 auto;
  min-height: 0;
}

/* Profile section at bottom of sidebar */
.side-menu-profile {
  border-top: 1px solid var(--sidebar-border);
  padding: 8px;
}

.profile-button {
  display: flex;
  align-items: center;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.profile-button:hover {
  background-color: var(--overlay-light);
}

.profile-avatar {
  background: var(--gradient-primary-blue-hover) !important;
  border: 2px solid var(--overlay-cyan-medium) !important;
  flex-shrink: 0;
}

.profile-initials {
  color: var(--button-text-light) !important;
  font-size: 11px !important;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.profile-info {
  margin-left: 10px;
  min-width: 0;
  flex: 1;
}

.profile-email {
  font-size: var(--font-size-xs);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.profile-plan {
  font-size: 10px;
  color: var(--text-secondary);
  font-weight: 500;
}
</style>

<style>
/* Color variables are now defined in src/styles/colors.css */

/* The side menu's collapse row and content share the drawer height (see
   .side-menu-content above). */
.side-menu .v-navigation-drawer__content {
  display: flex;
  flex-direction: column;
}

* {
  font-family: var(--font-body) !important;
}

.v-application {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  font-family: var(--font-body) !important;
}

/* Display face for headlines. Cards' titles stay in the body face - the
   display face is reserved for page-level headlines and the wordmark. */
h1,
h2,
.display-font {
  font-family: var(--font-display) !important;
  letter-spacing: -0.02em;
}

/* The shared account cards (Billing, Subscription) take the app's card-title
   face: the body face unless the app sets --font-card-title, e.g. to
   var(--font-display) when its page titles use the display face. */
.v-application .account-card-title {
  font-family: var(--font-card-title, var(--font-body)) !important;
}

/* Vuetify dark defaults (#121212 / #1E1E1E greys) → our surfaces, so
   menus, cards, sheets and inputs sit on the same near-black as the app. */
.theme--dark.v-card,
.theme--dark.v-sheet,
.theme--dark.v-list,
.theme--dark.v-menu__content,
.theme--dark.v-expansion-panel,
.theme--dark.v-data-table,
.theme--dark.v-dialog > .v-card,
.theme--dark.v-picker__body,
.theme--dark.v-stepper,
.theme--dark.v-tabs-items {
  background-color: var(--bg-card) !important;
  color: var(--text-primary) !important;
}
.theme--dark.v-card.v-sheet--outlined,
.theme--dark.v-card--outlined {
  border-color: var(--border-color) !important;
}
.theme--dark.v-list-item:hover::before {
  opacity: 0.06;
}
.theme--dark.v-text-field--outlined:not(.v-input--is-focused):not(.v-input--has-state)
  > .v-input__control
  > .v-input__slot
  fieldset {
  color: var(--border-input) !important;
}
.theme--dark.v-text-field--outlined.v-input--is-focused fieldset {
  color: var(--accent) !important;
}
.theme--dark.v-input input,
.theme--dark.v-input textarea {
  color: var(--text-primary) !important;
}
.theme--dark.v-label {
  color: var(--text-secondary) !important;
}
.theme--dark.v-chip:not(.v-chip--active) {
  background: var(--bg-tertiary);
}
.theme--dark.v-divider {
  border-color: var(--border-color) !important;
}
.theme--dark.v-snack__wrapper {
  background: var(--bg-card-alt) !important;
  border: 1px solid var(--border-color);
}
.theme--dark.v-tooltip__content {
  background: var(--bg-card-alt) !important;
  border: 1px solid var(--border-color);
  color: var(--text-primary) !important;
}
.theme--dark.v-btn--outlined {
  border-color: var(--border-input) !important;
}
.theme--dark.v-btn--outlined.primary--text {
  border-color: rgba(var(--rgb-accent), 0.55) !important;
}
.theme--dark.v-btn.v-btn--text:not(.primary--text):not(.error--text):not(.warning--text) {
  color: var(--text-primary) !important;
}

.v-btn {
  padding-left: 12px !important;
  padding-right: 12px !important;
  min-width: 0 !important;
  border-radius: 7px !important;
  font-weight: 600 !important;
}

/* Restore Vuetify's `block` behaviour. The min-width:0 above has the same
   specificity as Vuetify's own `.v-btn--block { min-width: 100% }` but a
   later source order, so it silently shrank EVERY block button in every
   app (auth.css carried a per-button workaround for this). */
.v-btn.v-btn--block {
  min-width: 100% !important;
}

/* Primary and Success buttons - gradient style */
.v-btn.primary,
.v-btn--primary,
.v-btn.v-btn--is-elevated.primary,
.v-btn.success,
.v-btn--success,
.v-btn.v-btn--is-elevated.success,
.v-btn.primary.v-btn--has-bg,
.v-btn.success.v-btn--has-bg,
button.v-btn.primary,
button.v-btn.success {
  background: var(--gradient-button) !important;
  background-color: transparent !important;
  color: var(--button-text-dark) !important;
  border: none !important;
}

.v-btn.primary span,
.v-btn.success span,
.v-btn--primary span,
.v-btn--success span,
button.v-btn.primary span,
button.v-btn.success span {
  color: var(--button-text-dark) !important;
}

.v-btn.primary .v-btn__content,
.v-btn.success .v-btn__content,
.v-btn--primary .v-btn__content,
.v-btn--success .v-btn__content,
button.v-btn.primary .v-btn__content,
button.v-btn.success .v-btn__content {
  color: var(--button-text-dark) !important;
}

.v-btn.primary .v-btn__content *,
.v-btn.success .v-btn__content *,
button.v-btn.primary .v-btn__content *,
button.v-btn.success .v-btn__content * {
  color: var(--button-text-dark) !important;
}

.v-btn.primary .v-icon,
.v-btn--primary .v-icon,
.v-btn.success .v-icon,
.v-btn--success .v-icon,
button.v-btn.primary .v-icon,
button.v-btn.success .v-icon {
  color: var(--button-text-dark) !important;
}

/* Secondary buttons - also use gradient style */
.v-btn.secondary,
.v-btn--secondary,
.v-btn.v-btn--is-elevated.secondary,
.v-btn.secondary.v-btn--has-bg,
button.v-btn.secondary {
  background: var(--gradient-button) !important;
  background-color: transparent !important;
  color: var(--button-text-dark) !important;
  border: none !important;
}

.v-btn.secondary span,
.v-btn--secondary span,
button.v-btn.secondary span {
  color: var(--button-text-dark) !important;
}

.v-btn.secondary .v-btn__content,
.v-btn--secondary .v-btn__content,
button.v-btn.secondary .v-btn__content {
  color: var(--button-text-dark) !important;
}

.v-btn.secondary .v-btn__content *,
button.v-btn.secondary .v-btn__content * {
  color: var(--button-text-dark) !important;
}

.v-btn.secondary .v-icon,
.v-btn--secondary .v-icon,
button.v-btn.secondary .v-icon {
  color: var(--button-text-dark) !important;
}

/* Info buttons - also use gradient style */
.v-btn.info,
.v-btn--info,
.v-btn.v-btn--is-elevated.info,
.v-btn.info.v-btn--has-bg,
button.v-btn.info {
  background: var(--gradient-button) !important;
  background-color: transparent !important;
  color: var(--button-text-dark) !important;
  border: none !important;
}

.v-btn.info span,
.v-btn--info span,
button.v-btn.info span,
.v-btn.info .v-btn__content,
.v-btn--info .v-btn__content,
button.v-btn.info .v-btn__content,
.v-btn.info .v-btn__content *,
button.v-btn.info .v-btn__content * {
  color: var(--button-text-dark) !important;
}

.v-btn.info .v-icon,
.v-btn--info .v-icon,
button.v-btn.info .v-icon {
  color: var(--button-text-dark) !important;
}

.v-btn.error,
.v-btn--error {
  background: var(--gradient-stop-button) !important;
  color: var(--button-text-light) !important;
  border: none !important;
}

.v-btn.error .v-icon,
.v-btn--error .v-icon {
  color: var(--button-text-light) !important;
}

/* Disabled button styling - maintain gradient but with opacity */
/* Override Vuetify dark theme disabled color - exact override */
.theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined) {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined)
  .v-btn__content {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined)
  span {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled:not(.v-btn--flat):not(.v-btn--text):not(.v-btn--outlined)
  .v-icon {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled.primary,
.theme--dark.v-btn.v-btn--disabled.success,
.theme--dark.v-btn.v-btn--disabled.secondary,
.theme--dark.v-btn.v-btn--disabled.info,
.v-btn.primary.v-btn--disabled,
.v-btn.success.v-btn--disabled,
.v-btn.secondary.v-btn--disabled,
.v-btn.info.v-btn--disabled,
.v-btn.primary:disabled,
.v-btn.success:disabled,
.v-btn.secondary:disabled,
.v-btn.info:disabled,
.v-btn--disabled.primary,
.v-btn--disabled.success,
.v-btn--disabled.secondary,
.v-btn--disabled.info,
button.v-btn.primary:disabled,
button.v-btn.success:disabled,
button.v-btn.secondary:disabled,
button.v-btn.info:disabled {
  background: var(--gradient-button) !important;
  background-color: transparent !important;
  opacity: 0.5 !important;
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled.primary span,
.theme--dark.v-btn.v-btn--disabled.success span,
.theme--dark.v-btn.v-btn--disabled.secondary span,
.theme--dark.v-btn.v-btn--disabled.info span,
.v-btn.primary.v-btn--disabled span,
.v-btn.success.v-btn--disabled span,
.v-btn.secondary.v-btn--disabled span,
.v-btn.info.v-btn--disabled span,
.v-btn.primary:disabled span,
.v-btn.success:disabled span,
.v-btn.secondary:disabled span,
.v-btn.info:disabled span,
button.v-btn.primary:disabled span,
button.v-btn.success:disabled span,
button.v-btn.secondary:disabled span,
button.v-btn.info:disabled span {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled.primary .v-btn__content,
.theme--dark.v-btn.v-btn--disabled.success .v-btn__content,
.theme--dark.v-btn.v-btn--disabled.secondary .v-btn__content,
.theme--dark.v-btn.v-btn--disabled.info .v-btn__content,
.v-btn.primary.v-btn--disabled .v-btn__content,
.v-btn.success.v-btn--disabled .v-btn__content,
.v-btn.secondary.v-btn--disabled .v-btn__content,
.v-btn.info.v-btn--disabled .v-btn__content,
.v-btn.primary:disabled .v-btn__content,
.v-btn.success:disabled .v-btn__content,
.v-btn.secondary:disabled .v-btn__content,
.v-btn.info:disabled .v-btn__content,
button.v-btn.primary:disabled .v-btn__content,
button.v-btn.success:disabled .v-btn__content,
button.v-btn.secondary:disabled .v-btn__content,
button.v-btn.info:disabled .v-btn__content {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled.primary .v-btn__content *,
.theme--dark.v-btn.v-btn--disabled.success .v-btn__content *,
.theme--dark.v-btn.v-btn--disabled.secondary .v-btn__content *,
.theme--dark.v-btn.v-btn--disabled.info .v-btn__content *,
.v-btn.primary.v-btn--disabled .v-btn__content *,
.v-btn.success.v-btn--disabled .v-btn__content *,
.v-btn.secondary.v-btn--disabled .v-btn__content *,
.v-btn.info.v-btn--disabled .v-btn__content *,
.v-btn.primary:disabled .v-btn__content *,
.v-btn.success:disabled .v-btn__content *,
.v-btn.secondary:disabled .v-btn__content *,
.v-btn.info:disabled .v-btn__content *,
button.v-btn.primary:disabled .v-btn__content *,
button.v-btn.success:disabled .v-btn__content *,
button.v-btn.secondary:disabled .v-btn__content *,
button.v-btn.info:disabled .v-btn__content * {
  color: var(--button-text-dark) !important;
}

.theme--dark.v-btn.v-btn--disabled.primary .v-icon,
.theme--dark.v-btn.v-btn--disabled.success .v-icon,
.theme--dark.v-btn.v-btn--disabled.secondary .v-icon,
.theme--dark.v-btn.v-btn--disabled.info .v-icon,
.v-btn.primary.v-btn--disabled .v-icon,
.v-btn.success.v-btn--disabled .v-icon,
.v-btn.secondary.v-btn--disabled .v-icon,
.v-btn.info.v-btn--disabled .v-icon,
.v-btn.primary:disabled .v-icon,
.v-btn.success:disabled .v-icon,
.v-btn.secondary:disabled .v-icon,
.v-btn.info:disabled .v-icon,
button.v-btn.primary:disabled .v-icon,
button.v-btn.success:disabled .v-icon,
button.v-btn.secondary:disabled .v-icon,
button.v-btn.info:disabled .v-icon {
  color: var(--button-text-dark) !important;
}

/* Smaller tabs styling */
.v-tabs > .v-tabs-bar {
  height: 32px !important;
  background: var(--bg-card) !important;
  border-bottom: 1.5px solid var(--border-color) !important;
}
.v-tabs > .v-tabs-bar .v-tab {
  min-width: 0 !important;
  padding: 0 12px !important;
  font-size: var(--font-size-xs) !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  color: var(--text-secondary) !important;
  font-weight: 600 !important;
  height: 32px !important;
}
.v-tabs > .v-tabs-bar .v-tab--active {
  color: var(--text-heading) !important;
  font-weight: 700 !important;
  background: var(--overlay-cyan-subtle) !important;
}
.v-tabs > .v-tabs-bar .v-tab .v-icon {
  font-size: 14px !important;
  margin-right: 4px !important;
  color: inherit !important;
}
/* Mobile navigation icon tabs - larger icons */
.icon-tabs.v-tabs > .v-tabs-bar .v-tab .v-icon {
  font-size: 20px !important;
  margin-right: 0 !important;
}
.v-tabs > .v-tabs-bar .v-tabs-slider {
  background-color: var(--text-heading) !important;
  height: 2px !important;
}

/* Card styling */
.v-card {
  background: var(--bg-primary) !important;
  border: none !important;
  border-radius: 0 !important;
}

.v-card.v-sheet {
  background: var(--bg-primary) !important;
}

.v-card__text {
  color: var(--text-primary) !important;
  background: var(--bg-primary) !important;
}

.v-card__actions {
  background: var(--bg-primary) !important;
}

/* Input and textarea styling */
.v-text-field input,
.v-textarea textarea,
.v-select .v-select__selection {
  color: var(--text-primary) !important;
  caret-color: var(--text-heading) !important;
  font-size: var(--font-size-sm) !important;
  font-weight: 400 !important;
}

.v-text-field:not(.v-text-field--outlined) .v-input__slot,
.v-textarea:not(.v-textarea--outlined) .v-input__slot,
.v-select:not(.v-select--outlined) .v-input__slot {
  border: 1.6px solid var(--border-input) !important;
  border-radius: 8px !important;
}

.v-text-field:not(.v-text-field--outlined) .v-input__slot::before,
.v-textarea:not(.v-textarea--outlined) .v-input__slot::before,
.v-select:not(.v-select--outlined) .v-input__slot::before {
  border: none !important;
}

.v-text-field:not(.v-text-field--outlined) .v-input__slot::after,
.v-textarea:not(.v-textarea--outlined) .v-input__slot::after,
.v-select:not(.v-select--outlined) .v-input__slot::after {
  border: none !important;
}

.v-text-field:not(.v-text-field--outlined).v-input--is-focused .v-input__slot,
.v-textarea:not(.v-textarea--outlined).v-input--is-focused .v-input__slot,
.v-select:not(.v-select--outlined).v-input--is-focused .v-input__slot {
  border-color: var(--text-heading) !important;
}

.v-text-field .v-label,
.v-textarea .v-label,
.v-select .v-label {
  color: var(--text-heading) !important;
  font-weight: 600 !important;
  font-size: var(--font-size-sm) !important;
}

.v-select .v-input__slot,
.v-text-field .v-input__slot,
.v-textarea .v-input__slot,
.v-autocomplete .v-input__slot {
  background: var(--input-surface) !important;
}

/* Outlined input styling */
.v-text-field--outlined .v-input__slot,
.v-select--outlined .v-input__slot,
.v-autocomplete--outlined .v-input__slot,
.v-textarea--outlined .v-input__slot {
  background: var(--input-surface) !important;
}

.v-text-field--outlined fieldset,
.v-select--outlined fieldset,
.v-autocomplete--outlined fieldset,
.v-textarea--outlined fieldset {
  border-color: var(--border-input) !important;
  border-width: 1.6px !important;
}

.v-text-field--outlined.v-input--is-focused fieldset,
.v-select--outlined.v-input--is-focused fieldset,
.v-autocomplete--outlined.v-input--is-focused fieldset,
.v-textarea--outlined.v-input--is-focused fieldset {
  border-color: var(--text-heading) !important;
  border-width: 2px !important;
}

/* .v-label--active {
  margin-top: -8px !important;
} */

.v-text-field label.v-label,
.v-textarea label.v-label,
.v-select label.v-label {
  color: var(--text-heading) !important;
  font-weight: 700 !important;
}

.v-select .v-icon {
  color: var(--text-heading) !important;
}

/* Placeholder text */
.v-text-field input::placeholder,
.v-textarea textarea::placeholder {
  color: var(--text-secondary) !important;
  opacity: 0.7 !important;
}

/* Label and subtitle styling */
.text-subtitle-2,
label {
  color: var(--text-heading) !important;
  font-weight: 600 !important;
  font-size: var(--font-size-sm) !important;
  margin-bottom: 8px !important;
  display: inline-block;
}

.v-label {
  color: var(--text-heading) !important;
  font-weight: 500 !important;
}

/* Fix label spacing in forms */
.v-text-field,
.v-textarea,
.v-select,
.v-autocomplete {
  margin-top: 8px !important;
}

.text-caption {
  color: var(--text-secondary) !important;
  font-size: var(--font-size-xs) !important;
}

/* Fix spacing for Vuetify form field details (caption text, helper text, error messages) */
.v-input__details {
  margin-bottom: 8px !important;
  padding-top: 4px !important;
}

.v-messages {
  margin-bottom: 8px !important;
}

/* Improve overall text readability */
p,
span,
div {
  color: var(--text-primary);
}

strong {
  color: var(--text-heading);
  font-weight: 700;
}

/* Dropdown menu styling */
.v-menu__content {
  background: var(--bg-card) !important;
  border: 1.5px solid var(--border-color) !important;
  border-radius: 8px !important;
  z-index: 1100 !important;
}

.v-list {
  background: var(--bg-card) !important;
  color: var(--text-primary) !important;
}

.v-list-item {
  color: var(--text-primary) !important;
}

.v-list-item:hover {
  background: var(--overlay-subtle) !important;
}

.v-list-item--active {
  background: var(--overlay-light) !important;
  color: var(--text-heading) !important;
}

/* Shadows with blue tint */
.v-card,
.v-btn,
.v-dialog {
  box-shadow: 0 2px 10px 0 var(--shadow-blue-tint) !important;
}

/* Data table styling */
.v-data-table {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
}

.v-data-table thead {
  background: var(--bg-card) !important;
}

.v-data-table tbody {
  background: var(--bg-primary) !important;
}

.v-data-table th {
  background: var(--bg-card) !important;
  color: var(--text-heading) !important;
  border-bottom: 1.5px solid var(--border-color) !important;
}

.v-data-table td {
  color: var(--text-primary);
  border-bottom: 1.5px solid var(--border-color) !important;
}

.v-data-table td span {
  color: inherit;
}

.v-data-table tr:hover {
  background: var(--overlay-cyan-subtle) !important;
}

.v-data-table__wrapper {
  background: var(--bg-primary) !important;
}

/* Data table footer (pagination) */
.v-data-footer {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  border-top: 1.5px solid var(--border-color) !important;
}

.v-data-footer__select .v-select {
  margin-left: 8px !important;
}

.v-data-footer__select .v-select .v-input__control {
  min-height: 32px !important;
}

.v-data-footer__select .v-select .v-input__slot {
  padding-left: 12px !important;
  padding-right: 8px !important;
}

.v-data-footer__pagination {
  color: var(--text-primary) !important;
}

/* Pagination */
.v-pagination .v-pagination__item,
.v-pagination .v-pagination__navigation {
  background: var(--bg-card) !important;
  color: var(--text-primary) !important;
}

.v-pagination .v-pagination__item--active {
  background: var(--gradient-button) !important;
  color: var(--button-text-dark) !important;
}

/* Tooltip styling */
.v-tooltip__content {
  background: var(--bg-card) !important;
  color: var(--text-primary) !important;
  border: 1.5px solid var(--border-color) !important;
  border-radius: 7px !important;
  font-size: var(--font-size-xs) !important;
  padding: 8px 12px !important;
}

/* Dialog styling */
.v-dialog {
  background: var(--bg-primary) !important;
}

.v-dialog .v-card {
  background: var(--bg-card) !important;
  border: 1.5px solid var(--border-color) !important;
  border-radius: 10px !important;
}

.v-dialog .v-card__title {
  background: var(--bg-card) !important;
  color: var(--text-heading) !important;
  border-bottom: 1.5px solid var(--border-color) !important;
}

.v-dialog .v-card__text {
  background: var(--bg-primary) !important;
}

.v-dialog .v-card__actions {
  background: var(--bg-primary) !important;
  border-top: 1.5px solid var(--border-color) !important;
}

/* GLOBAL MAINTENANCE MODE banner - unmissable, on every page while engaged. */
.maintenance-banner {
  background: #e65100;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-align: center;
  padding: 10px 16px;
  position: sticky;
  top: 0;
  z-index: 400; /* above the toolbar (300) - never hidden */
}

/* Ensure dialogs and overlays appear above toolbar (z-index: 300) */
.v-dialog__content {
  z-index: 1000 !important;
}

/* The flat 1000 above kills Vuetify's per-dialog stacking (DOM order wins),
   so a dialog that opens on top of ANOTHER dialog needs its own layer.
   Apply via content-class="dialog-stack-2" (e.g. BrokerCredentialsDialog).
   NB: Vuetify puts content-class on the inner .v-dialog frame, so the
   full-screen container is reached via :has(). Its real v-overlay is stuck
   at 999 (below the first dialog's 1000), so the container doubles as the
   backdrop: it dims the dialog beneath and catches clicks (pointer-events)
   so they can't land on the covered dialog. */
.v-dialog__content--active:has(.dialog-stack-2) {
  z-index: 1006 !important;
  background: rgba(33, 33, 33, 0.46);
  pointer-events: auto;
}
.v-dialog.dialog-stack-2 {
  border: 1.5px solid var(--border-color);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.55);
}

.v-overlay {
  z-index: 999 !important;
}

/* Scrollbar styling for dark theme */
::-webkit-scrollbar {
  width: 10px;
  height: 10px;
}

::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--border-scrollbar);
  border-radius: 4px;
  border: 2px solid var(--bg-secondary);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--border-scrollbar-hover);
}

/* Override browser autofill styling globally */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 1000px var(--input-surface) inset !important;
  -webkit-text-fill-color: var(--text-primary) !important;
  caret-color: var(--text-primary) !important;
  background-color: var(--input-surface) !important;
  transition: background-color 9999s ease-out !important;
}
</style>
